import { Component, HostListener, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom, Observable } from 'rxjs';
import { fileNameOf, movePath, renamePath, sanitizeFolder } from './file-path.util';
import { AuthService } from 'src/app/service/auth.service';
import { VcuService } from 'src/app/service/vcu.service';
import { FileService } from 'src/app/service/file.service';
import { ProjectService } from 'src/app/service/project.service';
import { VcuFileItem } from 'src/app/model/response/vcu-files-response';
import { VcuDevice } from 'src/app/model/vcu-pair.model';
import { FtFileType } from 'src/app/model/response/file-map-response';
import { SharedFileService } from 'src/app/service/shared-file.service';
import { ShareFileLinkRes } from 'src/app/model/response/shared-file';

@Component({
  selector: 'app-arquivos',
  templateUrl: './arquivos.component.html',
  styleUrls: ['./arquivos.component.css'],
})
export class ArquivosComponent implements OnInit {
  maps: VcuFileItem[] = [];
  logs: VcuFileItem[] = [];
  userID: string = '';
  loading = true;

  linkVcuDialogVisible = false;
  linkVcuFile: VcuFileItem | null = null;
  linkVcuFileType: 'map' | 'log' = 'map';
  vcuList: VcuDevice[] = [];
  selectedVcuId: string | null = null;
  loadingVcus = false;
  savingLink = false;

  uploadDialogVisible = false;
  selectedUploadFiles: File[] = [];
  uploading = false;
  uploadProgress = 0;
  /** Índice (0-based) do arquivo atualmente sendo enviado, para exibir progresso. */
  currentUploadIndex = 0;

  /** Pastas lógicas extraídas dos `pathFile` dos arquivos do usuário. */
  availableFolders: string[] = [];
  /** Modo de seleção da pasta destino no diálogo de upload. */
  folderChoice: 'root' | 'existing' | 'custom' = 'root';

  // ─── Renomear ────────────────────────────────────────────────────────
  renameDialogVisible = false;
  renameFile: VcuFileItem | null = null;
  /** Só o nome, sem extensão: a extensão aparece fixa ao lado do campo. */
  renameValue = "";
  renaming = false;

  // ─── Mover ───────────────────────────────────────────────────────────
  moveDialogVisible = false;
  moveItems: VcuFileItem[] = [];
  moveFolderChoice: 'root' | 'existing' | 'custom' = 'root';
  moveExistingFolder = "";
  moveCustomFolder = "";
  moving = false;
  selectedExistingFolder: string | null = null;
  customFolderName = '';

  // ─── Compartilhar link ───────────────────────────────────────────────
  shareDialogVisible = false;
  shareFileItem: VcuFileItem | null = null;
  shareLink: ShareFileLinkRes | null = null;
  shareUrl = '';
  sharing = false;

  constructor(
    private authService: AuthService,
    private vcuService: VcuService,
    private projectService: ProjectService,
    private fileService: FileService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private translocoService: TranslocoService,
    private titleService: Title,
    private router: Router,
    private sharedFileService: SharedFileService,
  ) {}

  ngOnInit(): void {
    this.setTitle();
    this.userID = this.authService.getUserInformations();
    if (this.userID) {
      this.vcuService.getUserFiles(this.userID).subscribe({
        next: (res) => {
          this.applyUserFiles(res?.listItens ?? []);
          this.loading = false;
        },
        error: () => {
          this.maps = [];
          this.logs = [];
          this.availableFolders = [];
          this.loading = false;
        },
      });
    } else {
      this.loading = false;
    }
  }

  private applyUserFiles(rawItems: VcuFileItem[]): void {
    const items = rawItems.filter((f) => !!f.pathFile);
    this.maps = items.filter((f) => (f.type || '').toLowerCase() === 'map');
    this.logs = items.filter((f) => (f.type || '').toLowerCase() === 'log');
    this.availableFolders = this.computeAvailableFolders(items);
  }

  /**
   * Extrai pastas lógicas únicas dos `pathFile`. Normaliza prefixo Windows
   * (`C:/FuelTech Vision/...`) e barras invertidas. Pasta vazia (= raiz) é ignorada.
   */
  private computeAvailableFolders(items: VcuFileItem[]): string[] {
    const set = new Set<string>();
    for (const item of items) {
      const folder = this.extractFolder(item.pathFile);
      if (folder) set.add(folder);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  private extractFolder(pathFile?: string | null): string {
    if (!pathFile) return '';
    const normalized = pathFile
      .replace(/^[A-Za-z]:[\\/]/, '')
      .replace(/^[\\/]+/, '')
      .replace(/\\/g, '/')
      .replace(/^FuelTech Vision\//i, '');
    const lastSlash = normalized.lastIndexOf('/');
    return lastSlash >= 0 ? normalized.slice(0, lastSlash) : '';
  }

  private sanitizeCustomFolder(value: string): string {
    return value
      .replace(/\\/g, '/')
      .replace(/^\/+|\/+$/g, '')
      .replace(/\/{2,}/g, '/')
      .trim();
  }

  /** Prefixo final do `pathFile` baseado na escolha do usuário. */
  private resolveFolderPrefix(): string {
    if (this.folderChoice === 'existing' && this.selectedExistingFolder) {
      return this.selectedExistingFolder;
    }
    if (this.folderChoice === 'custom') {
      return this.sanitizeCustomFolder(this.customFolderName);
    }
    return '';
  }

  get canStartUpload(): boolean {
    if (!this.selectedUploadFiles.length || this.uploading) return false;
    if (this.folderChoice === 'existing' && !this.selectedExistingFolder) return false;
    if (this.folderChoice === 'custom' && !this.sanitizeCustomFolder(this.customFolderName)) return false;
    return true;
  }

  setTitle() {
    this.translocoService
      .selectTranslate("title.archives")
      .subscribe((value) => this.titleService.setTitle(value));
  }

  onDownload(payload: { type: 'map' | 'log'; index: number; file: VcuFileItem }): void {
    this.downloadSingleFile(payload.type, payload.file);
  }

  async onDownloadMany(items: { type: 'map' | 'log'; file: VcuFileItem }[]): Promise<void> {
    for (const item of items) {
      await this.downloadSingleFile(item.type, item.file);
    }
  }

  private async downloadSingleFile(type: 'map' | 'log', file: VcuFileItem): Promise<void> {
    if (!file?.id || !this.userID) return;

    const folder = type === 'map' ? 'map' : 'log';
    const fileName = file.fileName ?? `${folder}_${file.id}`;
    const chunkSize = 10483712; // 10 MB

    try {
      const response = await lastValueFrom(
        this.vcuService.downloadUserFile(this.userID, folder, file.id, 0, chunkSize)
      );

      const contentRange = response.headers.get('Content-Range');
      const totalSize = contentRange ? parseInt(contentRange.split('/')[1], 10) : 0;
      const blobs: Blob[] = [response.body as Blob];

      if (totalSize > chunkSize) {
        const totalChunks = Math.ceil(totalSize / chunkSize);
        const promises: Promise<any>[] = [];
        for (let i = 1; i < totalChunks; i++) {
          const start = i * chunkSize + 1;
          const end = (i + 1) * chunkSize;
          promises.push(
            lastValueFrom(this.vcuService.downloadUserFile(this.userID, folder, file.id, start, end))
          );
        }
        const responses = await Promise.all(promises);
        responses.forEach((r) => { if (r?.body) blobs.push(r.body as Blob); });
      }

      this.downloadBlob(new Blob(blobs, { type: 'application/octet-stream' }), fileName);
    } catch (err: any) {
      const key = err?.status === 404 ? 'dashboard.files.fileNotFound' : 'errorConection';
      this.translocoService.selectTranslate(key).subscribe((msg) =>
        this.messageService.add({ key: 'notification', severity: err?.status === 404 ? 'warn' : 'error', summary: msg })
      ).unsubscribe();
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(url), 200);
  }

  /** Toast traduzido, no padrão já usado nas outras ações da tela. */
  private notify(severity: 'success' | 'warn' | 'error', key: string, params?: Record<string, unknown>): void {
    this.translocoService
      .selectTranslate(key, params)
      .subscribe((msg) => this.messageService.add({ key: 'notification', severity, summary: msg }))
      .unsubscribe();
  }

  // ─── Compartilhar link ─────────────────────────────────────────────────

  /** Cada abertura gera um código novo; os anteriores seguem valendo até expirar. */
  onShare(event: { type: 'map' | 'log'; file: VcuFileItem }): void {
    this.shareFileItem = event.file;
    this.shareLink = null;
    this.shareUrl = '';
    this.sharing = true;
    this.shareDialogVisible = true;
    this.sharedFileService.createLink(this.userID, event.file.id).subscribe({
      next: (res) => {
        this.sharing = false;
        this.shareLink = res;
        this.shareUrl = this.sharedFileService.pageUrl(res.code);
      },
      error: (err: HttpErrorResponse) => {
        this.sharing = false;
        this.shareDialogVisible = false;
        this.notify(err.status === 404 ? 'warn' : 'error', err.status === 404 ? 'arquivos.shareNotFound' : 'arquivos.shareError');
      },
    });
  }

  copyShareUrl(): void {
    if (!this.shareUrl) return;
    navigator.clipboard.writeText(this.shareUrl).then(
      () => this.notify('success', 'messages.success.linkCopied'),
      () => this.notify('error', 'arquivos.shareCopyError'),
    );
  }

  // ─── Renomear ──────────────────────────────────────────────────────────

  onRename(event: { type: 'map' | 'log'; file: VcuFileItem }): void {
    this.renameFile = event.file;
    const name = fileNameOf(event.file.pathFile) || event.file.fileName || "";
    const dot = name.lastIndexOf(".");
    this.renameValue = dot > 0 ? name.slice(0, dot) : name;
    this.renameDialogVisible = true;
  }

  /** Extensão exibida ao lado do campo, para o usuário não precisar digitá-la. */
  get renameExtension(): string {
    const name = fileNameOf(this.renameFile?.pathFile) || this.renameFile?.fileName || "";
    const dot = name.lastIndexOf(".");
    return dot > 0 ? name.slice(dot) : "";
  }

  get canConfirmRename(): boolean {
    return !this.renaming && this.renameValue.trim().length > 0;
  }

  confirmRename(): void {
    if (!this.renameFile || !this.canConfirmRename) return;
    const file = this.renameFile;
    const pathFile = renamePath(file.pathFile, this.renameValue);
    this.renaming = true;
    // `file` e `pathFile` juntos: o upload compõe os dois assim, e mandar só um deixaria
    // o nome da listagem desencontrado do caminho.
    this.vcuService.updateFile(this.userID, file.id, { file: fileNameOf(pathFile), pathFile })
      .subscribe({
        next: () => {
          this.renaming = false;
          this.renameDialogVisible = false;
          this.notify('success', 'arquivos.renameSuccess');
          this.onRefresh();
        },
        error: () => {
          this.renaming = false;
          this.notify('error', 'arquivos.renameError');
        },
      });
  }

  // ─── Mover ─────────────────────────────────────────────────────────────

  onMove(items: { type: 'map' | 'log'; file: VcuFileItem }[]): void {
    if (!items.length) return;
    this.moveItems = items.map((i) => i.file);
    this.moveFolderChoice = 'root';
    this.moveExistingFolder = "";
    this.moveCustomFolder = "";
    this.moveDialogVisible = true;
  }

  private resolveMoveFolder(): string {
    if (this.moveFolderChoice === 'existing') return this.moveExistingFolder;
    if (this.moveFolderChoice === 'custom') return sanitizeFolder(this.moveCustomFolder);
    return "";
  }

  get canConfirmMove(): boolean {
    if (this.moving || !this.moveItems.length) return false;
    if (this.moveFolderChoice === 'existing') return !!this.moveExistingFolder;
    if (this.moveFolderChoice === 'custom') return !!sanitizeFolder(this.moveCustomFolder);
    return true;
  }

  /**
   * Um PUT por arquivo, em sequência — não há operação de pasta no backend. Sem transação:
   * se parte falhar, a tela conta quantos moveram e quantos não, e a lista recarrega para
   * mostrar o estado real. Reverter os que já passaram seria outra rodada de chamadas que
   * também pode falhar, então o caminho honesto é relatar.
   */
  async confirmMove(): Promise<void> {
    if (!this.canConfirmMove) return;
    const folder = this.resolveMoveFolder();
    this.moving = true;
    let moved = 0;
    const failed: string[] = [];

    for (const file of this.moveItems) {
      const pathFile = movePath(file.pathFile, folder);
      try {
        await lastValueFrom(
          this.vcuService.updateFile(this.userID, file.id, { file: fileNameOf(pathFile), pathFile }),
        );
        moved++;
      } catch {
        failed.push(file.fileName || fileNameOf(file.pathFile));
      }
    }

    this.moving = false;
    this.moveDialogVisible = false;
    if (failed.length) {
      this.notify('warn', 'arquivos.movePartial', { moved, failed: failed.length });
    } else {
      this.notify('success', 'arquivos.moveSuccess', { moved });
    }
    this.onRefresh();
  }
  onLinkVcu(payload: { type: 'map' | 'log'; file: VcuFileItem }): void {
    this.linkVcuFile = payload.file;
    this.linkVcuFileType = payload.type;
    this.selectedVcuId = payload.file.vcuID ?? null;
    this.linkVcuDialogVisible = true;
    this.loadVcus();
  }

  onViewChannels(payload: { file: VcuFileItem }): void {
    if (!payload.file?.id) return;
    const fileType = this.resolveFtFileType(payload.file);
    this.router.navigate(['/archives/log', payload.file.id, 'channels'], {
      queryParams: { type: fileType },
    });
  }

  /**
   * Tipo aceito pelo read-log: .ftml/.ftlog (log+mapa) e .ftm/.ftmap (mapa standalone).
   *
   * A extensão é lida preferencialmente do `pathFile` (que traz nome+extensão de
   * forma confiável); `fileName` pode vir nulo/sem extensão. Se não der pra inferir
   * pela extensão, cai no TIPO conhecido do arquivo (map → ftmap; log → ftlog) —
   * NUNCA assume log por padrão, senão um mapa acaba abrindo na tela de log.
   */
  private resolveFtFileType(file: VcuFileItem): FtFileType {
    const source = file.pathFile || file.fileName || '';
    const dot = source.lastIndexOf('.');
    if (dot >= 0) {
      const ext = source.slice(dot + 1).toLowerCase().trim();
      const known = (['ftmap', 'ftm', 'ftml', 'ftlog'] as const).find((t) => t === ext);
      if (known) return known;
    }
    return (file.type || '').toLowerCase() === 'map' ? 'ftmap' : 'ftlog';
  }

  private loadVcus(): void {
    this.loadingVcus = true;
    this.vcuService.getPairedVcus().subscribe({
      next: (res) => {
        this.vcuList = res?.vcus ?? [];
        this.loadingVcus = false;
      },
      error: () => {
        this.vcuList = [];
        this.loadingVcus = false;
      },
    });
  }

  saveLinkVcu(): void {
    if (!this.linkVcuFile?.id || !this.selectedVcuId || !this.userID) return;
    this.savingLink = true;
    this.vcuService.updateFile(this.userID, this.linkVcuFile.id, { vcuID: this.selectedVcuId }).subscribe({
      next: () => {
        this.linkVcuFile!.vcuID = this.selectedVcuId;
        this.messageService.add({ key: 'notification', severity: 'success', summary: 'VCU vinculada com sucesso.' });
        this.linkVcuDialogVisible = false;
        this.savingLink = false;
      },
      error: () => {
        this.messageService.add({ key: 'notification', severity: 'error', summary: 'Erro ao vincular VCU.' });
        this.savingLink = false;
      },
    });
  }

  /** Ids dos arquivos com exclusão em andamento — mostra spinner no botão de excluir. */
  deletingIds = new Set<string>();

  /** Re-busca da lista em andamento (botão "atualizar"). */
  refreshing = false;

  /**
   * Atualiza a lista re-buscando no servidor SEM desmontar o explorer (não usa o
   * `loading` global), preservando a pasta/navegação atual — só traz itens novos.
   */
  onRefresh(): void {
    if (!this.userID || this.refreshing) return;
    this.refreshing = true;
    this.vcuService.getUserFiles(this.userID).subscribe({
      next: (res) => {
        this.applyUserFiles(res?.listItens ?? []);
        this.refreshing = false;
      },
      error: () => {
        this.refreshing = false;
      },
    });
  }

  onDeleteMany(items: { type: 'map' | 'log'; file: VcuFileItem }[]): void {
    if (!items.length || !this.userID) return;
    this.translocoService
      .selectTranslateObject('arquivos.deleteMany')
      .subscribe((value) => {
        this.confirmationService.confirm({
          message: value.message.replace('{{count}}', String(items.length)),
          header: value.header,
          icon: 'pi pi-exclamation-triangle',
          acceptButtonStyleClass: 'p-button-danger',
          rejectButtonStyleClass: 'p-button-text',
          acceptLabel: value.yes,
          rejectLabel: value.no,
          acceptIcon: 'none',
          rejectIcon: 'none',
          accept: () => this.executeDeleteMany(items),
          reject: () => {},
        });
      })
      .unsubscribe();
  }

  private async executeDeleteMany(items: { type: 'map' | 'log'; file: VcuFileItem }[]): Promise<void> {
    let deletedCount = 0;
    const deletedIds = new Set<string>();
    items.forEach(({ file }) => { if (file.id) this.deletingIds.add(file.id); });
    for (const { type, file } of items) {
      if (!file.id) continue;
      const obs$ = this.buildDeleteObservable(type, file);
      if (!obs$) { this.deletingIds.delete(file.id); continue; }
      try {
        await lastValueFrom(obs$);
        deletedIds.add(file.id);
        deletedCount++;
      } catch {
      } finally {
        this.deletingIds.delete(file.id);
      }
    }
    if (deletedCount > 0) {
      this.maps = this.maps.filter((f) => !deletedIds.has(f.id));
      this.logs = this.logs.filter((f) => !deletedIds.has(f.id));
    }
    this.translocoService
      .selectTranslate('arquivos.deleteManySuccess', { count: deletedCount })
      .subscribe((msg) =>
        this.messageService.add({ key: 'notification', severity: 'success', summary: msg })
      )
      .unsubscribe();
  }

  onDelete(payload: { type: 'map' | 'log'; index: number; file: VcuFileItem }): void {
    const { type, index, file } = payload;
    if (!file?.id || !this.userID) return;

    const isHardDelete = !file.vcuID && !file.projectID;
    const confirmKey = isHardDelete ? 'arquivos.deleteHard' : 'arquivos.delete';

    this.translocoService
      .selectTranslateObject(confirmKey)
      .subscribe((value) => {
        this.confirmationService.confirm({
          message: value.message,
          header: value.header,
          icon: isHardDelete ? 'pi pi-exclamation-triangle' : 'pi pi-info-circle',
          acceptButtonStyleClass: 'p-button-danger',
          rejectButtonStyleClass: 'p-button-text',
          acceptLabel: value.yes,
          rejectLabel: value.no,
          acceptIcon: 'none',
          rejectIcon: 'none',
          accept: () => this.executeDelete(type, index, file),
          reject: () => {},
        });
      })
      .unsubscribe();
  }

  private executeDelete(type: 'map' | 'log', index: number, file: VcuFileItem): void {
    const obs$ = this.buildDeleteObservable(type, file);
    if (!obs$) return;

    this.deletingIds.add(file.id); // loading no botão de excluir da linha
    obs$.subscribe({
      next: () => {
        this.deletingIds.delete(file.id);
        this.handleDeleteSuccess(type, index, file);
      },
      error: (err) => {
        this.deletingIds.delete(file.id);
        this.handleDeleteError(err);
      },
    });
  }

  private buildDeleteObservable(type: 'map' | 'log', file: VcuFileItem): Observable<void> | null {
    if (file.vcuID) {
      return this.vcuService.deleteVcuFile(this.userID, file.vcuID, type, file.id);
    }
    if (file.projectID) {
      return type === 'map'
        ? this.projectService.DeleteProjectMap(this.userID, file.projectID, file.id)
        : this.projectService.DeleteProjectLog(this.userID, file.projectID, file.id);
    }
    return this.vcuService.deleteUserFile(this.userID, file.id, type === 'map' ? 'MAP' : 'LOG');
  }

  private handleDeleteSuccess(type: 'map' | 'log', _index: number, file: VcuFileItem): void {
    if (type === 'map') {
      this.maps = this.maps.filter((f) => f.id !== file.id);
    } else {
      this.logs = this.logs.filter((f) => f.id !== file.id);
    }
    this.translocoService
      .selectTranslate('arquivos.deleteSuccess')
      .subscribe((msg) =>
        this.messageService.add({ key: 'notification', severity: 'success', summary: msg })
      )
      .unsubscribe();
  }

  private handleDeleteError(err: unknown): void {
    const status = err instanceof HttpErrorResponse ? err.status : 0;
    const key =
      status === 400 ? 'common.deleteErrors.badRequest'
      : status === 403 ? 'common.deleteErrors.forbidden'
      : status === 404 ? 'common.deleteErrors.notFound'
      : 'common.deleteErrors.generic';
    const severity = status === 404 ? 'warn' : 'error';

    this.translocoService
      .selectTranslate(key)
      .subscribe((msg) =>
        this.messageService.add({ key: 'notification', severity, summary: msg })
      )
      .unsubscribe();

    if (status === 404) this.refreshFiles();
  }

  openUploadDialog(): void {
    this.selectedUploadFiles = [];
    this.uploadProgress = 0;
    this.currentUploadIndex = 0;
    this.uploading = false;
    this.folderChoice = 'root';
    this.selectedExistingFolder = null;
    this.customFolderName = '';
    this.uploadDialogVisible = true;
  }

  isDragOver = false;
  isPageDragOver = false;
  private dragEnterCount = 0;

  @HostListener('document:dragenter', ['$event'])
  onDocDragEnter(e: DragEvent): void {
    if (!e.dataTransfer?.types.includes('Files') || this.uploadDialogVisible) return;
    this.dragEnterCount++;
    this.isPageDragOver = true;
  }

  @HostListener('document:dragleave', ['$event'])
  onDocDragLeave(): void {
    this.dragEnterCount = Math.max(0, this.dragEnterCount - 1);
    if (this.dragEnterCount === 0) this.isPageDragOver = false;
  }

  @HostListener('document:dragover', ['$event'])
  onDocDragOver(e: DragEvent): void {
    if (this.isPageDragOver) e.preventDefault();
  }

  @HostListener('document:drop', ['$event'])
  onDocDrop(e: DragEvent): void {
    e.preventDefault();
    this.dragEnterCount = 0;
    this.isPageDragOver = false;
    if (this.uploadDialogVisible) return;
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (!files.length) return;
    this.openUploadDialog();
    this.addFiles(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.uploading) this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (this.uploading) return;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length) this.addFiles(files);
  }

  onUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (!files.length) return;
    this.addFiles(files);
  }

  private addFiles(files: File[]): void {
    const accepted: File[] = [];
    let rejected = 0;
    for (const f of files) {
      if (!this.detectTypeFromExtension(f.name)) {
        rejected++;
        continue;
      }
      // Evita duplicar arquivos já adicionados (mesmo nome + tamanho)
      const isDup = this.selectedUploadFiles.some(
        (s) => s.name === f.name && s.size === f.size
      );
      if (!isDup) accepted.push(f);
    }

    if (rejected) {
      this.messageService.add({
        key: 'notification',
        severity: 'error',
        summary: this.translocoService.translate('arquivos.invalidFileType'),
        detail: this.translocoService.translate('arquivos.invalidFileTypeDetail'),
      });
    }
    if (accepted.length) {
      this.selectedUploadFiles = [...this.selectedUploadFiles, ...accepted];
    }
  }

  removeUploadFile(index: number): void {
    if (this.uploading) return;
    this.selectedUploadFiles = this.selectedUploadFiles.filter((_, i) => i !== index);
  }

  /**
   * .ftm/.ftmap → map; .ftml/.ftlog → log; demais → null (rejeitado).
   * Público para uso no template.
   */
  detectTypeFromExtension(fileName: string): 'map' | 'log' | null {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    if (ext === 'ftm' || ext === 'ftmap') return 'map';
    if (ext === 'ftml' || ext === 'ftlog') return 'log';
    return null;
  }

  formatBytes(bytes: number): string {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  }

  async startUpload(): Promise<void> {
    if (!this.canStartUpload || !this.userID) return;
    const files = this.selectedUploadFiles;
    const pathPrefix = this.resolveFolderPrefix();

    this.uploading = true;
    this.uploadProgress = 0;
    this.currentUploadIndex = 0;

    let succeeded = 0;
    let failed = 0;

    for (let i = 0; i < files.length; i++) {
      this.currentUploadIndex = i;
      this.uploadProgress = 0;
      const file = files[i];
      const type = this.detectTypeFromExtension(file.name);
      if (!type) {
        failed++;
        continue;
      }
      try {
        await this.fileService.uploadUserFile(
          this.userID,
          type,
          file,
          (percent) => {
            this.uploadProgress = percent;
          },
          pathPrefix
        );
        succeeded++;
      } catch {
        failed++;
      }
    }

    this.uploading = false;
    this.uploadDialogVisible = false;

    if (failed === 0) {
      this.messageService.add({
        key: 'notification',
        severity: 'success',
        summary: this.translocoService.translate('arquivos.uploadSuccess'),
        detail:
          succeeded > 1
            ? this.translocoService.translate('arquivos.uploadSuccessCount', { '0': succeeded })
            : undefined,
      });
    } else if (succeeded === 0) {
      this.messageService.add({
        key: 'notification',
        severity: 'error',
        summary: this.translocoService.translate('arquivos.uploadError'),
      });
    } else {
      this.messageService.add({
        key: 'notification',
        severity: 'warn',
        summary: this.translocoService.translate('arquivos.uploadPartial', {
          '0': succeeded,
          '1': failed,
        }),
      });
    }
    this.refreshFiles();
  }

  private refreshFiles(): void {
    if (!this.userID) return;
    this.loading = true;
    this.vcuService.getUserFiles(this.userID).subscribe({
      next: (res) => {
        this.applyUserFiles(res?.listItens ?? []);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }
}
